import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';

import remarkGfm from 'remark-gfm';

import { SpacefillLogo } from '@spacefill/shared/src/components/logos/SpacefillLogo';
import { call, callStream } from '@spacefill/shared/src/utils/Backend';
import { SidePanel } from '@spacefill/uikit/src/components/SidePanel';

import illustrationImg from '../../assets/empty-states/empty-state-sync-rules.png';

const SUGGESTIONS = [
    'Quelles expéditions ont été expédiées plus tôt que prévu ce mois-ci ?',
    'Quelles sont mes 10 références les plus expédiées sur les 20 derniers jours ?',
    "Quel est le ratio d'incidents par rapport au nombre d'expéditions ce trimestre ?",
];

export const StarIcon = () => (
    <svg
        width={20}
        height={20}
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
    >
        <path
            d='M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z'
            stroke='currentColor'
            strokeWidth={1.5}
            strokeLinejoin='round'
            fill='none'
        />
        <path
            d='M19 15L19.5 17.5L22 18L19.5 18.5L19 21L18.5 18.5L16 18L18.5 17.5L19 15Z'
            stroke='currentColor'
            strokeWidth={1.5}
            strokeLinejoin='round'
            fill='none'
        />
        <path
            d='M5 3L5.4 4.6L7 5L5.4 5.4L5 7L4.6 5.4L3 5L4.6 4.6L5 3Z'
            stroke='currentColor'
            strokeWidth={1.5}
            strokeLinejoin='round'
            fill='none'
        />
    </svg>
);

const LOADING_DOTS_STYLE = `
@keyframes chatDotBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-4px); opacity: 1; }
}
.chat-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: #9ca3af; animation: chatDotBounce 1.2s infinite; }
.chat-dot:nth-child(2) { animation-delay: 0.2s; }
.chat-dot:nth-child(3) { animation-delay: 0.4s; }
`;

const LoadingDots = () => (
    <>
        <style>{LOADING_DOTS_STYLE}</style>
        <span style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
            <span className='chat-dot' />
            <span className='chat-dot' />
            <span className='chat-dot' />
        </span>
    </>
);

export function AnalyticsChatPanel({ isOpen, onClose }) {
    const setIsOpen = (val) => !val && onClose();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(null);
    const [ratings, setRatings] = useState({});
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const rateMessage = async (msgIndex, rating) => {
        const msg = messages[msgIndex];
        const question = messages.slice(0, msgIndex).findLast((m) => m.role === 'user')?.content ?? '';
        setRatings((prev) => ({ ...prev, [msgIndex]: rating }));
        await call('analytics/chat/rate', { question, answer: msg.content, rating }, 'POST');
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const sendMessage = async (messageText, confirmedSql) => {
        const text = messageText ?? input.trim();
        if (!text || isLoading) return;

        const userMessage = { role: 'user', content: text };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        setLoadingStatus('thinking');

        try {
            const response = await callStream('analytics/chat/message', {
                message: text,
                confirmedSql,
                history: messages.map(({ role, content }) => ({ role, content })),
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop(); // garder le dernier fragment incomplet

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const event = JSON.parse(line.slice(6));

                    if (event.type === 'status') {
                        setLoadingStatus(event.label);
                    } else if (event.type === 'result') {
                        setMessages((prev) => [
                            ...prev,
                            {
                                role: 'assistant',
                                content: event.content,
                                requiresConfirmation: event.requiresConfirmation,
                                sql: event.sql,
                            },
                        ]);
                    }
                }
            }
        } catch (error) {
            setMessages((prev) => [
                ...prev,
                { role: 'assistant', content: `Erreur : ${error?.message ?? 'Une erreur est survenue.'}` },
            ]);
        } finally {
            setIsLoading(false);
            setLoadingStatus(null);
            setTimeout(scrollToBottom, 100);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const isEmpty = messages.length === 0;

    return createPortal(
        <SidePanel
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title={
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: 600 }}>
                    <StarIcon />
                    Interrogez vos données Spacefill
                </span>
            }
            width={680}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Zone messages ou état vide */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: isEmpty ? '0' : '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    {isEmpty ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '48px 32px 24px',
                                gap: '8px',
                            }}
                        >
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <img
                                    src={illustrationImg}
                                    alt=''
                                    style={{ width: '240px', pointerEvents: 'none' }}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '57%',
                                        left: '53%',
                                        transform: 'translate(-50%, -50%)',
                                        overflow: 'hidden',
                                        width: '72px',
                                        height: '36px',
                                    }}
                                >
                                    <SpacefillLogo
                                        style={{
                                            height: '36px',
                                            minWidth: 'max-content',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </div>
                            </div>

                            <p
                                style={{
                                    color: '#9ca3af',
                                    textAlign: 'center',
                                    fontSize: '13px',
                                    lineHeight: '1.6',
                                    margin: 0,
                                    maxWidth: '300px',
                                }}
                            >
                                Obtenez des réponses précises sur vos expéditions, réceptions et incidents — sans créer
                                de chart ni écrire de requête SQL.
                            </p>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, i) => (
                                <div
                                    key={i}
                                    style={{
                                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        maxWidth: '85%',
                                    }}
                                >
                                    <div
                                        style={{
                                            background: msg.role === 'user' ? '#111827' : '#f3f4f6',
                                            color: msg.role === 'user' ? 'white' : '#111827',
                                            borderRadius: '12px',
                                            padding: '10px 14px',
                                            fontSize: '14px',
                                            lineHeight: '1.5',
                                        }}
                                    >
                                        {msg.role === 'user' ? (
                                            msg.content
                                        ) : (
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    table: (props) => (
                                                        <div style={{ overflowX: 'auto' }}>
                                                            <table
                                                                style={{
                                                                    borderCollapse: 'collapse',
                                                                    width: '100%',
                                                                    fontSize: '13px',
                                                                }}
                                                                {...props}
                                                            />
                                                        </div>
                                                    ),
                                                    th: (props) => (
                                                        <th
                                                            style={{
                                                                border: '1px solid #d1d5db',
                                                                padding: '4px 8px',
                                                                background: '#e5e7eb',
                                                                textAlign: 'left',
                                                            }}
                                                            {...props}
                                                        />
                                                    ),
                                                    td: (props) => (
                                                        <td
                                                            style={{
                                                                border: '1px solid #d1d5db',
                                                                padding: '4px 8px',
                                                            }}
                                                            {...props}
                                                        />
                                                    ),
                                                    code: ({ inline, children, ...props }) =>
                                                        inline ? (
                                                            <code
                                                                style={{
                                                                    background: '#e5e7eb',
                                                                    borderRadius: '3px',
                                                                    padding: '1px 4px',
                                                                    fontSize: '12px',
                                                                }}
                                                                {...props}
                                                            >
                                                                {children}
                                                            </code>
                                                        ) : (
                                                            <pre
                                                                style={{
                                                                    background: '#e5e7eb',
                                                                    borderRadius: '6px',
                                                                    padding: '8px',
                                                                    overflowX: 'auto',
                                                                    fontSize: '12px',
                                                                }}
                                                            >
                                                                <code {...props}>{children}</code>
                                                            </pre>
                                                        ),
                                                }}
                                            >
                                                {msg.content}
                                            </ReactMarkdown>
                                        )}
                                    </div>
                                    {msg.role === 'assistant' && msg.sql && (
                                        <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                                            {['up', 'down'].map((dir) => (
                                                <button
                                                    key={dir}
                                                    onClick={() => rateMessage(i, dir)}
                                                    disabled={ratings[i] != null}
                                                    title={dir === 'up' ? 'Bonne réponse' : 'Mauvaise réponse'}
                                                    style={{
                                                        background:
                                                            ratings[i] === dir
                                                                ? dir === 'up'
                                                                    ? '#dcfce7'
                                                                    : '#fee2e2'
                                                                : '#f3f4f6',
                                                        border: '1px solid',
                                                        borderColor:
                                                            ratings[i] === dir
                                                                ? dir === 'up'
                                                                    ? '#86efac'
                                                                    : '#fca5a5'
                                                                : '#e5e7eb',
                                                        borderRadius: '6px',
                                                        padding: '3px 7px',
                                                        cursor: ratings[i] != null ? 'default' : 'pointer',
                                                        fontSize: '13px',
                                                        lineHeight: 1,
                                                        opacity: ratings[i] != null && ratings[i] !== dir ? 0.4 : 1,
                                                        transition: 'all 0.15s',
                                                    }}
                                                >
                                                    {dir === 'up' ? '👍' : '👎'}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {isLoading && (
                                <div
                                    style={{
                                        alignSelf: 'flex-start',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '10px 14px',
                                        background: '#f3f4f6',
                                        borderRadius: '12px',
                                        fontSize: '13px',
                                        color: '#6b7280',
                                    }}
                                >
                                    <LoadingDots />
                                    {loadingStatus === 'thinking' && 'Réflexion...'}
                                    {loadingStatus === 'running' && 'Exécution...'}
                                    {loadingStatus === 'prettifying' && 'Interprétation des résultats...'}
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Zone input + suggestions */}
                <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Textarea avec bouton intégré */}
                    <div
                        style={{
                            position: 'relative',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            background: 'white',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        }}
                    >
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder='Demandez une analyse sur vos données...'
                            disabled={isLoading}
                            rows={3}
                            style={{
                                width: '100%',
                                resize: 'none',
                                border: 'none',
                                borderRadius: '12px',
                                padding: '12px 52px 12px 14px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                outline: 'none',
                                boxSizing: 'border-box',
                                lineHeight: '1.5',
                                color: '#111827',
                            }}
                        />
                        <button
                            onClick={() => sendMessage()}
                            disabled={isLoading || !input.trim()}
                            style={{
                                position: 'absolute',
                                bottom: '10px',
                                right: '10px',
                                background: isLoading || !input.trim() ? '#d1d5db' : '#111827',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                width: '32px',
                                height: '32px',
                                cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background 0.15s',
                            }}
                        >
                            <svg
                                width={14}
                                height={14}
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M12 19V5M5 12L12 5L19 12'
                                    stroke='white'
                                    strokeWidth={2.5}
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Suggestions (affichées uniquement si conversation vide) */}
                    {isEmpty && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {SUGGESTIONS.map((suggestion, i) => (
                                <button
                                    key={i}
                                    onClick={() => sendMessage(suggestion)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '10px',
                                        padding: '10px 14px',
                                        fontSize: '13px',
                                        color: '#374151',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        lineHeight: '1.4',
                                        gap: '12px',
                                    }}
                                >
                                    <span>{suggestion}</span>
                                    <span
                                        style={{
                                            flexShrink: 0,
                                            background: '#84cc16',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '16px',
                                            lineHeight: 1,
                                        }}
                                    >
                                        +
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SidePanel>,
        document.body
    );
}
